import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import { toast } from "react-toastify";

import {
  getEnrollments,
  addEnrollment,
  editEnrollment,
  deleteEnrollment,
  getSubjectsByGrade,
  getStudentsByGrades,
} from "../../api/enrollmentApi";
import { getStudents } from "../../api/studentApi";
import { getSessions, getActiveSession } from "../../api/sessionApi";
import { getGrades } from "../../api/gradeApi";
import { getUsersByRole } from "../../common/api/userApi";
import DynamicDataTable from "../../common/components/Common/DataListing";
import { printEnrollmentSlip } from "../../common/utils/printEnrollmentSlip";

const EnrollmentList = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [grades, setGrades] = useState([]);
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [mandatorySubjects, setMandatorySubjects] = useState([]);
  const [electiveSubjects, setElectiveSubjects] = useState([]);

  // Toggle between existing student and new student
  const [studentMode, setStudentMode] = useState("existing"); // "existing" or "new"
  const studentModeOptions = [
    { label: "Existing Student", value: "existing" },
    { label: "New Student", value: "new" },
  ];

  const [formData, setFormData] = useState({
    id: null,
    student_id: null,
    session_id: null,
    grade_id: null,
    subject_ids: [],
    status: "active",
    enrolled_at: new Date(),
  });

  // New student form data
  const [newStudentData, setNewStudentData] = useState({
    full_name: "",
    gender: null,
    birth_date: null,
    parent_id: null,
  });

  useEffect(() => {
    fetchEnrollmentsData();
    fetchLookups();
  }, []);

  const fetchEnrollmentsData = async () => {
    setLoading(true);
    try {
      const res = await getEnrollments();
      const data = res?.data || res || [];
      setEnrollments(data);
    } catch (err) {
      console.error("❌ Error loading enrollments:", err);
      toast.error("Failed to load enrollments");
    } finally {
      setLoading(false);
    }
  };

  const fetchLookups = async () => {
    try {
      const [gr, , as] = await Promise.all([
        getGrades(),
        getSessions(),
        getActiveSession(),
      ]);
      setGrades(gr?.data || gr || []);
      setActiveSession(as?.data || as || null);

      // Fetch parents (role ID 4 is typically Parent role)
      try {
        const parentsList = await getUsersByRole(4);
        setParents(parentsList?.data || parentsList || []);
      } catch {
        // Parents fetch might fail due to auth - that's okay
        setParents([]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = () => {
    setIsEditing(false);
    setStudentMode("existing");
    setFormData({
      id: null,
      student_id: null,
      session_id: activeSession?.id || null,
      grade_id: null,
      subject_ids: [],
      status: "active",
      enrolled_at: new Date(),
    });
    setNewStudentData({
      full_name: "",
      gender: null,
      birth_date: null,
      parent_id: null,
    });
    setStudents([]);
    setMandatorySubjects([]);
    setElectiveSubjects([]);
    setIsModalVisible(true);
  };

  const handleEdit = async (row) => {
    try {
      setIsEditing(true);
      setLoading(true);

      const gradeId = row.grade?.id || row.grade_id;
      const [studentsList, subjectsList] = await Promise.all([
        getStudentsByGrades(gradeId),
        getSubjectsByGrade(gradeId),
      ]);

      const subjects = (subjectsList?.data || subjectsList || []).map((s) => ({
        id: s.id,
        name: s.name,
        is_mandatory: !!s.is_mandatory,
      }));

      const mandatory = subjects.filter((s) => s.is_mandatory);
      const elective = subjects.filter((s) => !s.is_mandatory);

      setStudents(studentsList?.data || studentsList || []);
      setMandatorySubjects(mandatory);
      setElectiveSubjects(elective);

      const selectedSubjects = row.subjects?.map((s) => s.id) || [];

      setFormData({
        id: row.id,
        student_id: row.student?.id || row.student_id,
        session_id: row.session?.id || row.session_id || activeSession?.id,
        grade_id: gradeId,
        subject_ids: selectedSubjects,
        status: row.status || "active",
        enrolled_at: row.enrolled_at ? new Date(row.enrolled_at) : new Date(),
      });

      setIsModalVisible(true);
    } catch (err) {
      console.error("❌ Edit load error:", err);
      toast.error("Failed to load data for edit");
    } finally {
      setLoading(false);
    }
  };

  const handleGradeChange = async (gradeId) => {
    setFormData((prev) => ({ ...prev, grade_id: gradeId, student_id: null }));
    await loadStudentsAndSubjects(gradeId);
  };

  const loadStudentsAndSubjects = async (gradeId) => {
    if (!gradeId) return;
    try {
      const [studentsList, subjectsList] = await Promise.all([
        getStudentsByGrades(gradeId),
        getSubjectsByGrade(gradeId),
      ]);

      setStudents(studentsList?.data || studentsList || []);

      const subjects = (subjectsList?.data || subjectsList || []).map((s) => ({
        id: s.id,
        name: s.name,
        is_mandatory: !!s.is_mandatory,
      }));

      const mandatory = subjects.filter((s) => s.is_mandatory);
      const elective = subjects.filter((s) => !s.is_mandatory);

      setMandatorySubjects(mandatory);
      setElectiveSubjects(elective);

      setFormData((prev) => ({
        ...prev,
        subject_ids: mandatory.map((s) => s.id),
      }));
    } catch (err) {
      console.error("❌ Load subjects/students error:", err);
      toast.error("Failed to load students/subjects");
    }
  };

  // ✅ FULLY FIXED DELETE FUNCTION
  const handleDelete = async (id) => {
    // const id = row?.id;
    if (!id) {
      toast.error("Invalid enrollment selected for delete");
      return;
    }

    try {
      setLoading(true);
     

      const res = await deleteEnrollment(id);

      if (res?.success) {
        toast.success(res?.message || "Enrollment deleted successfully");
        // ✅ remove from frontend state instantly
        setEnrollments((prev) => prev.filter((e) => e.id !== id));
      } else {
        toast.error(res?.message || "Failed to delete enrollment");
      }
    } catch (err) {
      console.error("❌ Delete failed:", err);
      toast.error("Failed to delete enrollment");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation based on mode
    if (studentMode === "existing") {
      if (!formData.student_id || !formData.grade_id || !formData.subject_ids.length) {
        toast.error("All required fields must be filled");
        return;
      }
    } else {
      // New student mode
      if (!newStudentData.full_name || !newStudentData.gender) {
        toast.error("Student name and gender are required");
        return;
      }
      if (!formData.grade_id || !formData.subject_ids.length) {
        toast.error("Grade and subjects are required");
        return;
      }
    }

    try {
      setLoading(true);

      // Check for duplicate only when enrolling existing student
      if (studentMode === "existing" && !isEditing) {
        const duplicate = enrollments.find(
          (e) =>
            e.student?.id === formData.student_id &&
            e.grade?.id === formData.grade_id &&
            (e.session?.id || activeSession?.id) ===
              (formData.session_id || activeSession?.id)
        );

        if (duplicate) {
          toast.error("Enrollment for this student, grade, and session already exists.");
          setLoading(false);
          return;
        }
      }

      let payload;

      if (isEditing) {
        // Edit mode - always uses existing student
        payload = {
          student_id: formData.student_id,
          session_id: activeSession?.id || formData.session_id,
          grade_id: formData.grade_id,
          subject_ids: formData.subject_ids,
          status: formData.status,
          enrolled_at: formData.enrolled_at,
        };

        await editEnrollment(formData.id, payload);
        toast.success("Enrollment updated successfully");
        setEnrollments((prev) =>
          prev.map((e) => (e.id === formData.id ? { ...e, ...formData } : e))
        );
      } else if (studentMode === "new") {
        // New student with enrollment
        payload = {
          student: {
            full_name: newStudentData.full_name,
            gender: newStudentData.gender,
            birth_date: newStudentData.birth_date,
            parent_id: newStudentData.parent_id,
          },
          session_id: activeSession?.id || formData.session_id,
          grade_id: formData.grade_id,
          subject_ids: formData.subject_ids,
          status: formData.status,
          enrolled_at: formData.enrolled_at,
        };

        const res = await addEnrollment(payload);

        if (res.student_created) {
          toast.success("Student and enrollment created successfully!");
        } else {
          toast.success("Enrollment created successfully");
        }

        // Use response data for the new enrollment
        if (res.data) {
          setEnrollments((prev) => [res.data, ...prev]);
        } else {
          // Fallback to manual construction
          const newEnrollment = {
            id: res?.groupId || Date.now(),
            student: { full_name: newStudentData.full_name },
            grade: grades.find((g) => g.id === formData.grade_id),
            subjects: mandatorySubjects.concat(
              electiveSubjects.filter((s) => formData.subject_ids.includes(s.id))
            ),
            status: formData.status,
            enrolled_at: formData.enrolled_at,
          };
          setEnrollments((prev) => [newEnrollment, ...prev]);
        }
      } else {
        // Existing student enrollment
        payload = {
          student_id: formData.student_id,
          session_id: activeSession?.id || formData.session_id,
          grade_id: formData.grade_id,
          subject_ids: formData.subject_ids,
          status: formData.status,
          enrolled_at: formData.enrolled_at,
        };

        const res = await addEnrollment(payload);
        toast.success("Enrollment added successfully");

        // Use response data if available
        if (res.data) {
          setEnrollments((prev) => [res.data, ...prev]);
        } else {
          const newEnrollment = {
            id: res?.groupId || Date.now(),
            student: students.find((s) => s.id === formData.student_id),
            grade: grades.find((g) => g.id === formData.grade_id),
            subjects: mandatorySubjects.concat(
              electiveSubjects.filter((s) => formData.subject_ids.includes(s.id))
            ),
            status: formData.status,
            enrolled_at: formData.enrolled_at,
          };
          setEnrollments((prev) => [newEnrollment, ...prev]);
        }
      }

      setIsModalVisible(false);
    } catch (err) {
      console.error("❌ Save failed:", err);
      const errorMsg = err?.message || "Failed to save enrollment";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (row) => {
    const ok = await printEnrollmentSlip({
      studentName: row.student?.full_name,
      photo: row.student?.photo,
      enrollmentNo: row.enrollment_no,
      sessionName: row.session?.name || activeSession?.name,
      gradeName: row.grade?.name,
      subjects: row.subjects?.map((s) => s.name).join(", "),
      status: row.status,
      enrolledAt: row.enrolled_at ? new Date(row.enrolled_at).toLocaleDateString() : "",
    });
    if (!ok) toast.error("Pop-up blocked. Allow pop-ups to print.");
  };


  const subjectOptions = [
    {
      label: "Mandatory Subjects",
      items: mandatorySubjects.map((s) => ({
        label: s.name,
        value: s.id,
        disabled: true,
      })),
    },
    {
      label: "Elective Subjects",
      items: electiveSubjects.map((s) => ({
        label: s.name,
        value: s.id,
      })),
    },
  ];

  return (
    <div>
      <DynamicDataTable
        data={enrollments}
        subject="Enrollment"
        columns={[
          { field: "student.full_name", header: "Student" },
          { field: "grade.name", header: "Grade" },
          {
            header: "Subjects",
            body: (row) => row.subjects?.map((s) => s.name).join(", ") || "-",
          },
          { field: "status", header: "Status" },
          {
            header: "Enrolled At",
            body: (row) =>
              row.enrolled_at
                ? new Date(row.enrolled_at).toLocaleDateString()
                : "",
          },
        ]}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        extraActions={[
          {
            label: "Print",
            icon: "pi pi-print",
            className: "p-button-secondary p-button-sm",
            handler: handlePrint,
          },
        ]}
        loading={loading}
      />

      <Dialog
        header={isEditing ? "Edit Enrollment" : "New Enrollment"}
        visible={isModalVisible}
        style={{ width: "55vw" }}
        onHide={() => setIsModalVisible(false)}
      >
        <div className="p-fluid">
          {/* Student Mode Toggle - Only show when creating */}
          {!isEditing && (
            <div className="mb-4">
              <label className="font-semibold mb-2 block">Student Type</label>
              <SelectButton
                value={studentMode}
                options={studentModeOptions}
                onChange={(e) => {
                  setStudentMode(e.value);
                  setFormData((prev) => ({ ...prev, student_id: null }));
                }}
              />
            </div>
          )}

          <div className="grid">
            <div className="col-6">
              <label className="font-semibold mb-2 block">Grade *</label>
              <Dropdown
                value={formData.grade_id}
                options={grades.map((g) => ({
                  label: g.name,
                  value: g.id,
                }))}
                onChange={(e) => handleGradeChange(e.value)}
                placeholder="Select Grade"
              />
            </div>

            {/* Existing Student Selection */}
            {(studentMode === "existing" || isEditing) && (
              <div className="col-6">
                <label className="font-semibold mb-2 block">Student *</label>
                <Dropdown
                  value={formData.student_id}
                  options={students.map((s) => ({
                    label: s.full_name,
                    value: s.id,
                  }))}
                  onChange={(e) =>
                    setFormData({ ...formData, student_id: e.value })
                  }
                  placeholder="Select Student"
                  filter
                  disabled={!formData.grade_id}
                />
                {!formData.grade_id && (
                  <small className="text-gray-500">Select a grade first</small>
                )}
              </div>
            )}
          </div>

          {/* New Student Form - Only show when creating new student */}
          {studentMode === "new" && !isEditing && (
            <div className="surface-100 p-3 border-round mt-3">
              <div className="flex align-items-center gap-2 mb-3">
                <Tag value="New Student" severity="success" />
                <span className="text-sm text-gray-600">Enter student details below</span>
              </div>
              <div className="grid">
                <div className="col-6">
                  <label className="font-semibold mb-2 block">Full Name *</label>
                  <InputText
                    value={newStudentData.full_name}
                    onChange={(e) =>
                      setNewStudentData({ ...newStudentData, full_name: e.target.value })
                    }
                    placeholder="Enter student name"
                  />
                </div>
                <div className="col-6">
                  <label className="font-semibold mb-2 block">Gender *</label>
                  <Dropdown
                    value={newStudentData.gender}
                    options={[
                      { label: "Male", value: "Male" },
                      { label: "Female", value: "Female" },
                      { label: "Other", value: "Other" },
                    ]}
                    onChange={(e) =>
                      setNewStudentData({ ...newStudentData, gender: e.value })
                    }
                    placeholder="Select Gender"
                  />
                </div>
                <div className="col-6 mt-2">
                  <label className="font-semibold mb-2 block">Date of Birth</label>
                  <Calendar
                    value={newStudentData.birth_date}
                    onChange={(e) =>
                      setNewStudentData({ ...newStudentData, birth_date: e.value })
                    }
                    showIcon
                    placeholder="Select DOB"
                    maxDate={new Date()}
                  />
                </div>
                <div className="col-6 mt-2">
                  <label className="font-semibold mb-2 block">Parent/Guardian</label>
                  <Dropdown
                    value={newStudentData.parent_id}
                    options={parents.map((p) => ({
                      label: p.name || p.full_name || p.email,
                      value: p.id,
                    }))}
                    onChange={(e) =>
                      setNewStudentData({ ...newStudentData, parent_id: e.value })
                    }
                    placeholder="Select Parent (Optional)"
                    filter
                    showClear
                  />
                </div>
              </div>
            </div>
          )}

          <div className="mb-3 mt-3">
            <label className="font-semibold mb-2 block">Subjects</label>
            <MultiSelect
              value={formData.subject_ids}
              options={subjectOptions}
              optionGroupLabel="label"
              optionGroupChildren="items"
              onChange={(e) => {
                const selectedElectives = e.value.filter((id) =>
                  electiveSubjects.some((s) => s.id === id)
                );
                const allSelected = [
                  ...mandatorySubjects.map((s) => s.id),
                  ...selectedElectives,
                ];
                setFormData({ ...formData, subject_ids: allSelected });
              }}
              display="chip"
              placeholder="Select Subjects"
              className="w-full"
            />
          </div>

          <div className="grid mt-3">
            <div className="col-6">
              <label className="font-semibold mb-2 block">Status</label>
              <Dropdown
                value={formData.status}
                options={[
                  { label: "Active", value: "active" },
                  { label: "Completed", value: "completed" },
                  { label: "Cancelled", value: "cancelled" },
                ]}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.value })
                }
              />
            </div>

            <div className="col-6">
              <label className="font-semibold mb-2 block">Enrolled Date</label>
              <Calendar
                value={formData.enrolled_at}
                onChange={(e) =>
                  setFormData({ ...formData, enrolled_at: e.value })
                }
                showIcon
              />
            </div>
          </div>

          <div className="flex justify-content-end mt-3">
            <Button
              label={isEditing ? "Update" : "Add"}
              className="p-button-primary"
              onClick={handleSubmit}
              disabled={loading}
            />
          </div>

          {loading && (
            <div style={spinnerStyle}>
              <ProgressSpinner />
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
};

const spinnerStyle = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 1000,
};

export default EnrollmentList;
